import React from "react";
import type { AriaDateFieldOptions } from "@react-aria/datepicker";
import type { DateValue, TimeValue } from "react-aria";
import { useLocale } from "react-aria";
import { useDateFieldState } from "react-stately";
import { Time, createCalendar } from "@internationalized/date";
import { ListBox, ListBoxItem } from "react-aria-components";
import { colors } from "../Theme";
import Text from "../Text";
import { css } from "@linaria/core";

const StyledListBox = css`
  border-left: 1px solid ${colors.titanium100};
  max-height: 304px;
  overflow: auto;
  padding: 12px;
`;

export const StyledListBoxItem = css`
  scroll-margin-top: 10px;
  border-radius: 4px;
  padding: 8px;
  width: 56px;
  cursor: pointer;

  color: ${colors.carbon900};

  &:hover {
    background: ${colors.titanium100};
  }

  &:active,
  &:focus,
  &[data-selected] {
    background: ${colors.lightNitrogen50};
  }

  &:focus {
    outline: 2px solid ${colors.lightNitrogen100};
  }
`;

interface TimeSelectorProps<T extends DateValue> {
  dateFieldOptions: AriaDateFieldOptions<T>;
  value: TimeValue | undefined;
  setTimeValue: (value: TimeValue) => void;
}

function TimeSelector<T extends DateValue>({
  dateFieldOptions,
  value,
  setTimeValue,
}: TimeSelectorProps<T>) {
  const { locale } = useLocale();
  const state = useDateFieldState({
    ...dateFieldOptions,
    locale,
    createCalendar,
  });

  const [shouldShowHours, shouldShowMinutes, shouldShowSeconds] = React.useMemo(() => {
    const { segments } = state;

    return [
      segments.find(segment => segment.type === "hour") !== undefined,
      segments.find(segment => segment.type === "minute") !== undefined,
      segments.find(segment => segment.type === "second") !== undefined,
    ];
  }, [state]);

  const hours = React.useMemo(() => {
    const values = Array.from(Array(24).keys());
    return values.map(value => ({
      value,
      text: value.toString().padStart(2, "0"),
    }));
  }, []);

  const sixty = React.useMemo(() => {
    const values = Array.from(Array(60).keys());
    return values.map(value => ({
      value,
      text: value.toString().padStart(2, "0"),
    }));
  }, []);

  const hourDivRef = React.useRef<HTMLDivElement>(null);
  const minuteDivRef = React.useRef<HTMLDivElement>(null);
  const secondDivRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Hack so the scrollIntoView works
    window.setTimeout(() => {
      if (hourDivRef.current && value !== undefined && value !== null && value.hour !== undefined) {
        const selectedElement = hourDivRef.current.querySelector(`[data-key="${value.hour}"]`);
        selectedElement?.scrollIntoView({ block: "start" });
      }

      if (
        minuteDivRef.current &&
        value !== undefined &&
        value !== null &&
        value.minute !== undefined
      ) {
        const selectedElement = minuteDivRef.current.querySelector(`[data-key="${value.minute}"]`);
        selectedElement?.scrollIntoView({ block: "start" });
      }

      if (
        secondDivRef.current &&
        value !== undefined &&
        value !== null &&
        value.second !== undefined
      ) {
        const selectedElement = secondDivRef.current.querySelector(`[data-key="${value.second}"]`);
        selectedElement?.scrollIntoView({ block: "start" });
      }
    }, 0);
    // We actually want to run this effect only once, so we don't want to include value in the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {shouldShowHours && (
        <ListBox
          aria-label="hours"
          ref={hourDivRef}
          className={StyledListBox}
          selectedKeys={
            value !== undefined && value !== null && value.hour !== undefined ? [value.hour] : []
          }
          selectionMode="single"
          selectionBehavior="replace"
          disallowEmptySelection
          onSelectionChange={selection => {
            const [newValue] = selection;

            if (value === null || value === undefined) {
              setTimeValue(new Time(newValue as number, 0, 0));
            } else {
              setTimeValue(new Time(newValue as number, value.minute, value.second));
            }
          }}
        >
          {hours.map(hour => (
            <ListBoxItem
              className={StyledListBoxItem}
              key={hour.value}
              id={hour.value}
              textValue={hour.text}
            >
              <Text type="body1">{hour.text}</Text>
            </ListBoxItem>
          ))}
        </ListBox>
      )}
      {shouldShowMinutes && (
        <ListBox
          aria-label="minutes"
          ref={minuteDivRef}
          className={StyledListBox}
          selectedKeys={
            value !== undefined && value !== null && value.minute !== undefined
              ? [value.minute]
              : []
          }
          selectionMode="single"
          selectionBehavior="replace"
          disallowEmptySelection
          onSelectionChange={selection => {
            const [newValue] = selection;

            if (value === null || value === undefined) {
              setTimeValue(new Time(0, newValue as number, 0));
            } else {
              setTimeValue(new Time(value.hour, newValue as number, value.second));
            }
          }}
        >
          {sixty.map(minute => (
            <ListBoxItem
              className={StyledListBoxItem}
              key={minute.value}
              id={minute.value}
              textValue={minute.text}
            >
              <Text type="body1">{minute.text}</Text>
            </ListBoxItem>
          ))}
        </ListBox>
      )}
      {shouldShowSeconds && (
        <ListBox
          aria-label="secondes"
          ref={secondDivRef}
          className={StyledListBox}
          selectedKeys={
            value !== undefined && value !== null && value.second !== undefined
              ? [value.second]
              : []
          }
          selectionMode="single"
          selectionBehavior="replace"
          disallowEmptySelection
          onSelectionChange={selection => {
            const [newValue] = selection;
            if (value === null || value === undefined) {
              setTimeValue(new Time(0, 0, newValue as number));
            } else {
              setTimeValue(new Time(value.hour, value.minute, newValue as number));
            }
          }}
        >
          {sixty.map(second => (
            <ListBoxItem
              className={StyledListBoxItem}
              key={second.value}
              id={second.value}
              textValue={second.text}
            >
              <Text type="body1">{second.text}</Text>
            </ListBoxItem>
          ))}
        </ListBox>
      )}
    </>
  );
}

export default TimeSelector;
